import React, { Component } from 'react';
import styles from './styles.module.scss';
import CopyIcon from 'shared_features/svgs/copy';
import EmailIcon from 'shared_features/svgs/email';

class SharingView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            urlCopied: false,
            contactEmail: '',
            emailCopied: false,
            programUsers: [],
            loadingUsers: false,
            loadingEmails: {},
            copiedEmails: {}
        };
        this._copyTimer = null;
        this._emailCopyTimers = {};
    }

    componentDidMount() {
        const { program } = this.props;
        if (program && program.contact_email) {
            this.setState({ contactEmail: program.contact_email });
        }
        if (program && program.is_public) {
            this.fetchProgramUsers();
        }
    }

    componentDidUpdate(prevProps) {
        const { program } = this.props;
        if (program && prevProps.program !== program && program.contact_email !== this.state.contactEmail) {
            this.setState({ contactEmail: program.contact_email || '' });
        }
        // Fetch users when program exists and either:
        // - No previous program and current one is public (initial async load)
        // - Program ID changed (route navigation)
        // - Visibility changed from false to true
        if (program) {
            const prev = prevProps.program;
            if ((!prev && program.is_public) || (prev && (prev.id !== program.id || (!prev.is_public && program.is_public)))) {
                this.fetchProgramUsers();
            }
        }
    }

    fetchProgramUsers = async () => {
        const { program } = this.props;
        if (!program) return;

        this.setState({ loadingUsers: true });
        try {
            const response = await fetch(`/api/programs/${program.id}/users`, {
                credentials: 'include'
            });
            if (response.ok) {
                const users = await response.json();
                this.setState({ programUsers: users, loadingUsers: false });
            } else {
                this.setState({ loadingUsers: false });
            }
        } catch (error) {
            console.error('Failed to fetch program users:', error);
            const { displayErrorAlert } = await import('services/alerts');
            displayErrorAlert("Failed to load program users. Please check your internet connection and try again.");
            this.setState({ loadingUsers: false });
        }
    }

    handleMessageClick = async (athleteId) => {
        const { program } = this.props;

        if (this.state.loadingEmails[athleteId]) return;

        this.setState(prev => ({
            loadingEmails: { ...prev.loadingEmails, [athleteId]: true }
        }));

        try {
            const response = await fetch(`/api/programs/${program.id}/users/${athleteId}/email`, {
                credentials: 'include'
            });
            if (response.ok) {
                const { email } = await response.json();
                if (email) {
                    window.location.href = `mailto:${email}`;
                } else {
                    const { displayErrorAlert } = await import('services/alerts');
                    displayErrorAlert("We couldn't find an email address for this athlete. They may not have an account yet.");
                }
            } else if (response.status === 404) {
                const { displayErrorAlert } = await import('services/alerts');
                displayErrorAlert("We couldn't find this athlete or their email address. They may not have an account.");
            } else {
                const { displayErrorAlert } = await import('services/alerts');
                displayErrorAlert("Something went wrong while trying to get the athlete's email. Please try again.");
            }
        } catch (error) {
            console.error('Failed to fetch athlete email:', error);
            const { displayErrorAlert } = await import('services/alerts');
            displayErrorAlert("We couldn't connect to the server. Please check your internet connection and try again.");
        } finally {
            this.setState(prev => ({
                loadingEmails: { ...prev.loadingEmails, [athleteId]: false }
            }));
        }
    }

    handleCopyEmailClick = async (athleteId) => {
        const { program } = this.props;

        if (this.state.loadingEmails[athleteId]) return;

        this.setState(prev => ({
            loadingEmails: { ...prev.loadingEmails, [athleteId]: true }
        }));

        try {
            const response = await fetch(`/api/programs/${program.id}/users/${athleteId}/email`, {
                credentials: 'include'
            });
            if (response.ok) {
                const { email } = await response.json();
                if (email) {
                    await navigator.clipboard.writeText(email);

                    // Show "Copied!" state
                    this.setState(prev => ({
                        copiedEmails: { ...prev.copiedEmails, [athleteId]: true }
                    }));

                    // Clear existing timer for this athlete if any
                    if (this._emailCopyTimers[athleteId]) {
                        clearTimeout(this._emailCopyTimers[athleteId]);
                    }

                    // Reset after 2 seconds
                    this._emailCopyTimers[athleteId] = setTimeout(() => {
                        this.setState(prev => ({
                            copiedEmails: { ...prev.copiedEmails, [athleteId]: false }
                        }));
                        delete this._emailCopyTimers[athleteId];
                    }, 2000);
                } else {
                    const { displayErrorAlert } = await import('services/alerts');
                    displayErrorAlert("We couldn't find an email address for this athlete. They may not have an account yet.");
                }
            } else if (response.status === 404) {
                const { displayErrorAlert } = await import('services/alerts');
                displayErrorAlert("We couldn't find this athlete or their email address. They may not have an account.");
            } else {
                const { displayErrorAlert } = await import('services/alerts');
                displayErrorAlert("Something went wrong while trying to get the athlete's email. Please try again.");
            }
        } catch (error) {
            console.error('Failed to copy athlete email:', error);
            const { displayErrorAlert } = await import('services/alerts');
            displayErrorAlert("We couldn't connect to the server. Please check your internet connection and try again.");
        } finally {
            this.setState(prev => ({
                loadingEmails: { ...prev.loadingEmails, [athleteId]: false }
            }));
        }
    }

    formatLastActive = (lastActive) => {
        if (!lastActive) return 'Never';

        const now = new Date();
        const activeDate = new Date(lastActive);
        const diffMs = now - activeDate;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    }

    handleSharingToggle = () => {
        const { program, updateProgramSharing } = this.props;
        updateProgramSharing(program.id, !program.is_public);
    }

    handleContactEmailChange = (e) => {
        this.setState({ contactEmail: e.target.value });
    }

    handleContactEmailToggle = () => {
        const { program, updateProgramContactEmail } = this.props;
        const { contactEmail } = this.state;

        if (program.contact_email) {
            // Disable contact email
            updateProgramContactEmail(program.id, null);
            this.setState({ contactEmail: '' });
        } else {
            // Enable contact email with current value or default
            const emailToUse = contactEmail || program.authorInfo?.email || '';
            updateProgramContactEmail(program.id, emailToUse);
            this.setState({ contactEmail: emailToUse });
        }
    }

    handleContactEmailSave = () => {
        const { program, updateProgramContactEmail } = this.props;
        const { contactEmail } = this.state;
        updateProgramContactEmail(program.id, contactEmail);
    }

    copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            this.setState({ urlCopied: true });

            // Clear existing timer if any
            if (this._copyTimer) {
                clearTimeout(this._copyTimer);
            }

            // Set new timer and store the ID
            this._copyTimer = setTimeout(() => {
                this.setState({ urlCopied: false });
                this._copyTimer = null;
            }, 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }

    componentWillUnmount() {
        if (this._copyTimer) {
            clearTimeout(this._copyTimer);
            this._copyTimer = null;
        }
        Object.values(this._emailCopyTimers).forEach(timer => {
            clearTimeout(timer);
        });
        this._emailCopyTimers = {};
    }

    render() {
        const { program } = this.props;
        const { urlCopied, contactEmail, programUsers, loadingUsers, loadingEmails, copiedEmails } = this.state;

        if (!program) {
            return <div>Loading...</div>;
        }

        const publicUrl = `${window.location.origin}/shared/${program.id}`;

        return (
            <div className={styles.container}>
                <div className={styles.section}>
                    <h2>URL</h2>
                    <p className={styles.description}>This allows anyone with the link to view and copy the program to their personal organization.</p>
                        
                    <p className={styles.description}>To share programs within an organization, schedule the program to a group.</p>
                        
                    <p className={styles.description}>To share a program to athletes who are not yet on your roster, schedule the program to a group and share a group invite link with your athletes.</p>

                    <div className={styles.toggleContainer}>
                        <label className={styles.toggle}>
                            <input
                                type="checkbox"
                                checked={program.is_public || false}
                                onChange={this.handleSharingToggle}
                            />
                            <span className={styles.slider}></span>
                        </label>
                    </div>

                    {program.is_public && (
                        <>
                            <div className={styles.urlSection}>
                                <div className={styles.urlContainer}>
                                    <a
                                        href={publicUrl}
                                        className={styles.publicUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {publicUrl}
                                    </a>
                                    <button
                                        className={styles.copyButton}
                                        onClick={() => this.copyToClipboard(publicUrl)}
                                    >
                                        {urlCopied ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.usersSection}>
                                <h3 className={styles.usersTitle}>
                                    SHARED WITH {programUsers.length} ATHLETE{programUsers.length !== 1 ? 'S' : ''}
                                </h3>
                                {loadingUsers ? (
                                    <div className={styles.loadingText}>Loading...</div>
                                ) : programUsers.length === 0 ? (
                                    <div className={styles.emptyText}>No athletes are using this program yet</div>
                                ) : (
                                    <div className={styles.usersList}>
                                        {programUsers.map(user => (
                                            <div key={user.athlete_id} className={styles.userRow}>
                                                <div className={styles.userInfo}>
                                                    {user.avatar ? (
                                                        <img src={user.avatar} alt={user.name} className={styles.userAvatar} />
                                                    ) : (
                                                        <div className={styles.userAvatarPlaceholder}>
                                                            {user.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div className={styles.userDetails}>
                                                        <div className={styles.userName}>{user.name}</div>
                                                        <div className={styles.userLastActive}>
                                                            last active {this.formatLastActive(user.last_active)}
                                                        </div>
                                                    </div>
                                                </div>
                                                {user.account_id && (
                                                    <div className={styles.userActions}>
                                                        <button
                                                            className={styles.copyEmailButton}
                                                            onClick={() => this.handleCopyEmailClick(user.athlete_id)}
                                                            disabled={loadingEmails[user.athlete_id]}
                                                            title="Copy Email"
                                                        >
                                                            <CopyIcon className={styles.buttonIcon} />
                                                            <span className={styles.buttonText}>
                                                                {copiedEmails[user.athlete_id] ? 'Copied!' : 'COPY EMAIL'}
                                                            </span>
                                                        </button>
                                                        <button
                                                            className={styles.messageButton}
                                                            onClick={() => this.handleMessageClick(user.athlete_id)}
                                                            disabled={loadingEmails[user.athlete_id]}
                                                            title="Message"
                                                        >
                                                            <EmailIcon className={styles.buttonIcon} />
                                                            <span className={styles.buttonText}>
                                                                {loadingEmails[user.athlete_id] ? 'Loading...' : 'MESSAGE'}
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.section}>
                    <h2>Contact Button</h2>
                    <p className={styles.description}>
                        Allow viewers to contact you for coaching. This will expose your email address.
                    </p>

                    <div className={styles.toggleContainer}>
                        <label className={styles.toggle}>
                            <input
                                type="checkbox"
                                checked={!!program.contact_email}
                                onChange={this.handleContactEmailToggle}
                            />
                            <span className={styles.slider}></span>
                        </label>
                        <span className={styles.toggleLabel}>
                            Enable contact button
                        </span>
                    </div>

                    {program.contact_email && (
                        <div className={styles.emailSection}>
                            <div className={styles.emailContainer}>
                                <label className={styles.emailLabel}>
                                    Contact Email:
                                </label>
                                <input
                                    type="email"
                                    value={contactEmail}
                                    onChange={this.handleContactEmailChange}
                                    placeholder={program.authorInfo?.email || 'Enter email address'}
                                    className={styles.emailInput}
                                />
                                <button
                                    className={styles.saveButton}
                                    onClick={this.handleContactEmailSave}
                                    disabled={contactEmail === program.contact_email}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default SharingView;